import { memo, useCallback, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import PublicNavMobileLink from './PublicNavMobileLink';

function subscribeToPathname(callback) {
  window.addEventListener('popstate', callback);
  window.addEventListener('hashchange', callback);
  return () => {
    window.removeEventListener('popstate', callback);
    window.removeEventListener('hashchange', callback);
  };
}

function getPathnameSnapshot() {
  return window.location.pathname;
}

/**
 * Mobile nav — menu panel is always mounted (hidden when closed) so toggling
 * the hamburger does not mount/unmount React Router Link trees.
 */
const PublicNavMobileShell = memo(function PublicNavMobileShell({
  preparedLinks,
  design,
  isDark,
  containerWidth,
  heightClass,
  logoUrl,
  logoAlt,
  logoHeightPx,
  logoMaxWidth,
  siteName,
  isLoggedIn,
  dashboardPath,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useSyncExternalStore(subscribeToPathname, getPathnameSnapshot, () => '/');
  const navigate = useNavigate();

  const toggleMobileMenu = useCallback(() => {
    setMobileOpen((open) => !open);
  }, []);

  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  const mobilePanelClass = isDark
    ? 'bg-slate-900 border-white/10'
    : 'bg-white/95 backdrop-blur-xl border-indigo-50';

  const logoContent = logoUrl ? (
    <img
      src={logoUrl}
      alt={logoAlt}
      style={{ height: `${logoHeightPx}px`, maxWidth: `${logoMaxWidth}px` }}
      className="w-auto object-contain"
    />
  ) : (
    <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-indigo-600'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
      {siteName}
    </span>
  );

  return (
    <>
      <div className={`${containerWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div className={`flex items-center justify-between ${heightClass}`}>
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            {logoContent}
          </Link>
          <button
            type="button"
            onClick={toggleMobileMenu}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-slate-600 hover:bg-indigo-50'}`}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="public-nav-mobile-panel"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        id="public-nav-mobile-panel"
        className={`md:hidden border-t ${mobilePanelClass}${mobileOpen ? '' : ' hidden'}`}
        data-testid="nav-mobile-menu"
        aria-hidden={!mobileOpen}
      >
        <div className="px-4 py-4 space-y-1">
          {preparedLinks.map((link) => (
            <PublicNavMobileLink
              key={link.id}
              link={link}
              isDark={isDark}
              isActive={!link.hash && pathname === link.pathname}
              onNavigate={closeMobileMenu}
            />
          ))}
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            {isLoggedIn ? (
              <button
                type="button"
                onClick={() => { closeMobileMenu(); navigate(dashboardPath); }}
                className="w-full rounded-full bg-indigo-600 text-white py-2.5 text-sm font-medium"
                data-testid="nav-mobile-dashboard-btn"
              >
                Dashboard
              </button>
            ) : (
              <>
                {design.show_login_button && (
                  <button
                    type="button"
                    onClick={() => { closeMobileMenu(); navigate('/login'); }}
                    className="w-full text-center py-2.5 text-sm font-medium text-slate-600 hover:text-indigo-600"
                    data-testid="nav-mobile-login-btn"
                  >
                    Login
                  </button>
                )}
                {design.show_signup_cta && design.cta_label && design.cta_link && (
                  <button
                    type="button"
                    onClick={() => { closeMobileMenu(); navigate(design.cta_link); }}
                    className="w-full rounded-full bg-indigo-600 text-white py-2.5 text-sm font-medium"
                    data-testid="nav-mobile-cta-btn"
                  >
                    {design.cta_label}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default PublicNavMobileShell;
